import React from 'react';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import omitBy from 'lodash/omitBy';
import moment from 'moment';
import Leaflet from 'bf-library/Leaflet';

const getDisplayMode = (priceDisplayMode) => {
    const messages = {
        perNight          : <FormattedMessage id="bf.displayMode.avgPricePerNight" defaultMessage="bf.displayMode.avgPricePerNight" />,
        perPersonPerNight : <FormattedMessage
            id="bf.displayMode.avgPerPersonPerNight"
            defaultMessage="bf.displayMode.avgPerPersonPerNight"
        />,
        stay              : <FormattedMessage id="bf.displayMode.totalPrice" defaultMessage="bf.displayMode.totalPrice" />,
        default           : <FormattedMessage id="bf.displayMode" defaultMessage="bf.displayMode" />,
    };
    return messages[priceDisplayMode] || messages.default;
};

const getDisplayModeForProperties = (priceDisplayMode, nights) => {
    switch (priceDisplayMode) {
        case 'perNight':
            return <FormattedMessage id="bf.displayMode.avgPricePerNight" defaultMessage="bf.displayMode.avgPricePerNight" />;
        case 'perPersonPerNight':
            return <FormattedMessage id="bf.displayMode.avgPerPersonPerNight" defaultMessage="bf.displayMode.avgPerPersonPerNight" />;
        case 'stay':
            return <FormattedMessage id="bf.nightsCount" defaultMessage="bf.nightsCount" values={{ count: nights }} />;
        default:
            return <FormattedMessage id="bf.displayMode" defaultMessage="bf.displayMode" />;
    }
};

const getPaginatorReferenceLabel = (currentPage, pageSize, totalResults, intlItems) => {
    const firstHotelIndex = (currentPage - 1) * pageSize + 1;
    const lastHotelIndex = (currentPage * pageSize) > totalResults ? totalResults : currentPage * pageSize;

    const referenceLabel = (
        <FormattedMessage
            id="bf.pageReference"
            defaultMessage="bf.pageReference"
            values={{
                minIndex   : firstHotelIndex,
                maxIndex   : lastHotelIndex,
                totalItems : totalResults,
                items      : intlItems,
            }}
        />
    );

    return referenceLabel;
};

const defineWidthClass = (watchedElement) => {
    if (watchedElement < 500) {
        return 'width-less-500';
    }

    if (watchedElement < 600) {
        return 'width-less-600';
    }

    if (watchedElement < 700) {
        return 'width-less-700';
    }

    if (watchedElement < 800) {
        return 'width-less-800';
    }

    if (watchedElement < 900) {
        return 'width-less-900';
    }

    if (watchedElement < 1000) {
        return 'width-less-1000';
    }

    if (watchedElement < 1100) {
        return 'width-less-1100';
    }

    if (watchedElement < 1200) {
        return 'width-less-1200';
    }

    return '';
};

const inHotelProfilePage = () => (window.location.href.indexOf('/hotel-profile/') !== -1);

const getMissingMessage = (key, values = null) => values
    ? <FormattedMessage id={key} defaultMessage={key} values={values} />
    : <FormattedMessage id={key} defaultMessage={key} />;

const scrollToTop = (time, px) => {
    let intervalId = null;
    intervalId = setInterval(() => {
        if (window.pageYOffset === 0) {
            clearInterval(intervalId);
        }
        window.scroll(0, window.pageYOffset - px);
    }, time);
};

const getUpdatesLink = (language, isProduction) => {
    // Redirection url doesn't exist for spanish
    if (language === 'es') {
        return '';
    }
    const url = !isProduction ? `https://avc-cms-dev.kognitiv.com/${language === 'zh' ? 'zh-hans' : language}/updates/`
        : `https://www.anantaravacationclub.com/${language === 'zh' ? 'zh-hans' : language}/updates/`;
    return <FormattedHTMLMessage id="bf.clickHere" defaultMessage="bf.clickHere" values={{ link: url }} />;
};

const getNumberOfNights = (checkIn, checkOut) => {
    const checkInMoment = moment(checkIn, 'YYYY-MM-DD');
    const checkOutMoment = moment(checkOut, 'YYYY-MM-DD');
    return checkOutMoment.diff(checkInMoment, 'days');
};

const getPropertyCardProps = props => ({
    ...props.config,
    currency        : props.currency,
    currencyDisplay : props.currencyDisplay,
    newTabDisabled  : props.newTabDisabled,
    mapProvider     : Leaflet,
    onSelection     : () => {},
});

const getTrustYouProfiles = (
    prevOffers,
    setPrevOffers,
    offers,
    prevNonAvailableOffers,
    setPrevNonAvailableOffers,
    nonAvailableOffers,
    language,
    dispatchGetTrustYouProfile,
) => {
    const getTrustYouProfilesFromProperties = (properties) => {
        properties.forEach((property) => {
            if (property.trustYouId) {
                dispatchGetTrustYouProfile(property.trustYouId, language);
            }
        });
    };

    const manageNewResponse = (
        prevValue,
        newValue,
        setPrevValue,
    ) => {
        if (newValue && !isEqual(prevValue, newValue)) {
            setPrevValue(newValue);
            getTrustYouProfilesFromProperties(newValue);
        }
    };

    manageNewResponse(prevOffers, offers, setPrevOffers);
    manageNewResponse(prevNonAvailableOffers, nonAvailableOffers, setPrevNonAvailableOffers);
};

const filterByNoUndefined = object => pickBy(object, value => !isUndefined(value) && !isNull(value));

/**
 *
 * @param {object} object
 * @returns An object with the empty string keys and values removed
 */
const deleteEmptyStrings = object => (omitBy(object, v => v === ''));

/**
 * Builds the locale with the form language (ISO2): country (ISO2)
 * For example ES:US
 */
const getLocale = (language, country) => `${language.toUpperCase()}${country ? `-${country.toUpperCase()}` : ''}`;

export {
    getDisplayMode,
    defineWidthClass,
    inHotelProfilePage,
    getMissingMessage,
    scrollToTop,
    getUpdatesLink,
    getPropertyCardProps,
    getTrustYouProfiles,
    getNumberOfNights,
    getDisplayModeForProperties,
    getPaginatorReferenceLabel,
    filterByNoUndefined,
    deleteEmptyStrings,
    getLocale,
};
